import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Container, Button, Modal } from 'react-bootstrap';
import { useParams, useNavigate } from 'react-router-dom';
import { QRCodeScanner, EventScannerNav } from '../../components/scanner';
import { apiService } from '../../services/api';
import { PassScanResult } from '../../types';
import SkeletonLoader from '../../components/common/SkeletonLoader';
import './QRCodeScannerPage.scss';

const QRCodeScannerPage: React.FC = () => {
  const { eventSlug } = useParams<{ eventSlug: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [eventTitle, setEventTitle] = useState<string>('');
  const [scanResult, setScanResult] = useState<PassScanResult | null>(null);
  const [recentScans, setRecentScans] = useState<PassScanResult[]>([]);
  const [isScanning, setIsScanning] = useState(true);
  const [showResultModal, setShowResultModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [showInstructionsModal, setShowInstructionsModal] = useState(false);
  const [facingMode, setFacingMode] = useState<'user' | 'environment'>('environment');
  
  // Prevent duplicate scans
  const isProcessingRef = useRef(false);
  const lastScannedIdentifierRef = useRef<string | null>(null);
  const lastScanTimeRef = useRef<number>(0);
  const scanCooldownMs = 3000; // 3 seconds cooldown between scans

  useEffect(() => {
    const fetchEventData = async () => {
      if (!eventSlug) return;

      try {
        setLoading(true);
        const data = await apiService.scanner.getEventScannerData(eventSlug);
        if (data.success && data.event) {
          setEventTitle(data.event.title);
        }
      } catch (err: any) {
        setError(err.message || 'Failed to load event data');
      } finally {
        setLoading(false);
      }
    };

    fetchEventData();
  }, [eventSlug]);

  // Extract UUID from QR code data (might be a URL or direct UUID)
  const extractUuidFromData = (data: string): string => {
    // Check if the data is a URL
    try {
      const url = new URL(data);
      
      // Check if this is a pass lookup URL pattern: /pass/lookup?uuid=...
      if (url.pathname.includes('/pass/lookup')) {
        const uuidParam = url.searchParams.get('uuid');
        if (uuidParam) {
          return uuidParam;
        }
      }
      
      // Fallback: try to find UUID in query parameters
      const uuidParam = url.searchParams.get('uuid') || url.searchParams.get('id');
      if (uuidParam) {
        return uuidParam;
      }
    } catch (e) {
      // If it's not a valid URL, treat as direct UUID
    }
    
    // Return data as-is if it's not a URL or no UUID found
    return data;
  };

  const handleScan = useCallback(async (decodedText: string) => {
    if (!eventSlug || !decodedText) return;

    // Stop scanning immediately - scanner component will handle stopping
    setIsScanning(false);

    // Extract UUID from the QR code data (might be a URL or direct UUID)
    const identifier = extractUuidFromData(decodedText);

    // Prevent duplicate scans
    const now = Date.now();
    const timeSinceLastScan = now - lastScanTimeRef.current;
    
    // If we're already processing a scan, ignore
    if (isProcessingRef.current) {
      return;
    }
    
    // If this is the same identifier and we scanned recently, ignore
    if (lastScannedIdentifierRef.current === identifier && timeSinceLastScan < scanCooldownMs) {
      // Resume scanning if it's a duplicate
      setIsScanning(true);
      return;
    }

    // Mark as processing
    isProcessingRef.current = true;
    lastScannedIdentifierRef.current = identifier;
    lastScanTimeRef.current = now;

    try {
      const result = await apiService.scanner.scanPass(eventSlug, identifier);
      setScanResult(result);
      setShowResultModal(true);
      
      // Add to recent scans if successful
      if (result.success) {
        setRecentScans((prev) => [result, ...prev.slice(0, 4)]);
      }
    } catch (err: any) {
      console.error('Scan error:', err);
      
      // Extract error message from API response
      let errorMessage = 'Failed to scan pass';
      if (err.response) {
        // API returned an error response
        if (err.response.status === 404) {
          errorMessage = err.response.data?.message || 'Pass not found. Please check the QR code.';
        } else if (err.response.status === 403) {
          errorMessage = err.response.data?.message || 'You do not have permission to scan this pass.';
        } else {
          errorMessage = err.response.data?.message || err.response.data?.error || `Error: ${err.response.status}`;
        }
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
      
      // Show error in modal if available
      if (err.response?.data) {
        setScanResult({
          success: false,
          message: errorMessage,
          identifier: identifier,
        } as PassScanResult);
        setShowResultModal(true);
      } else {
        // If no response data, resume scanning after error
        setTimeout(() => {
          setIsScanning(true);
        }, 1000);
      }
    } finally {
      // Reset processing flag after a delay to allow modal to be shown
      setTimeout(() => {
        isProcessingRef.current = false;
      }, 500);
    }
  }, [eventSlug, scanCooldownMs]);

  const handleContinueScanning = useCallback(() => {
    setShowResultModal(false);
    setScanResult(null);
    setIsScanning(true);
    // Reset the last scanned identifier to allow re-scanning the same code after modal closes
    setTimeout(() => {
      lastScannedIdentifierRef.current = null;
    }, 1000);
  }, []);

  const handleScanError = (errorMessage: string) => {
    // Filter out expected errors that occur during normal scanning
    // These are not actual errors - they just mean no QR code was detected yet
    const expectedErrors = [
      'No QR code found',
      'NotFoundException',
      'No MultiFormat Readers were able to detect the code',
      'QR code parse error',
    ];
    
    const isExpectedError = expectedErrors.some(expected => 
      errorMessage && errorMessage.includes(expected)
    );
    
    // Only log actual errors, not expected scanning behavior
    if (!isExpectedError && errorMessage) {
      console.warn('Scanner error:', errorMessage);
    }
  };

  const formatScanResult = (result: PassScanResult) => {
    return (
      <div className="scan-details">
        <div className="row">
          <div className="col-md-6">
            <div className="info-group mb-2">
              <label className="text-muted">Name</label>
              <h6 className="mb-0">{result.name || 'N/A'}</h6>
            </div>
            <div className="info-group mb-2">
              <label className="text-muted">Category</label>
              <h6 className="mb-0">{result.category || 'N/A'}</h6>
            </div>
            {result.seat_info && (
              <div className="info-group mb-2">
                <label className="text-muted">Seat Information</label>
                <h6 className="mb-0">{result.seat_info.number}</h6>
              </div>
            )}
            <div className="info-group mb-2">
              <label className="text-muted">Email</label>
              <h6 className="mb-0">{result.email || 'N/A'}</h6>
            </div>
          </div>
          <div className="col-md-6">
            <div className="info-group mb-2">
              <label className="text-muted">Phone</label>
              <h6 className="mb-0">{result.phone || 'N/A'}</h6>
            </div>
            {result.scanned_by && (
              <div className="info-group mb-2">
                <label className="text-muted">Scanned By</label>
                <h6 className="mb-0">{result.scanned_by}</h6>
              </div>
            )}
            {result.scanned_on && (
              <div className="info-group mb-2">
                <label className="text-muted">Scanned On</label>
                <h6 className="mb-0">{result.scanned_on}</h6>
              </div>
            )}
          </div>
        </div>
        {result.additional_fields && Object.keys(result.additional_fields).length > 0 && (
          <div className="row mt-3">
            <div className="col-12">
              <h6 className="text-muted mb-2 small">Additional Information</h6>
              {Object.entries(result.additional_fields).map(([label, value]) => (
                <div key={label} className="info-group mb-2">
                  <label className="text-muted small">{label}</label>
                  <h6 className="mb-0">{String(value)}</h6>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  // Show error if eventSlug is missing
  if (!eventSlug) {
    return (
      <div className="qr-scanner-page">
        <Container className="py-2">
          <div className="alert alert-danger">
            <h4>Invalid Route</h4>
            <p>Event slug is missing from the URL. Please navigate from the scanner dashboard.</p>
            <button className="btn btn-primary" onClick={() => navigate('/scanner/')}>
              Go to Scanner Dashboard
            </button>
          </div>
        </Container>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="qr-scanner-page">
        <Container className="py-2">
          <SkeletonLoader />
        </Container>
      </div>
    );
  }

  if (error && !eventTitle) {
    return (
      <div className="qr-scanner-page">
        <Container className="py-2">
          <div className="alert alert-danger">
            <h4>Error Loading Event</h4>
            <p>{error}</p>
            <button className="btn btn-primary" onClick={() => navigate('/scanner/')}>
              Go to Scanner Dashboard
            </button>
          </div>
        </Container>
      </div>
    );
  }

  return (
    <div className="qr-scanner-page">
      <Container className="py-2">
        {/* Header with Back Button */}
        <div className="scanner-header-wrapper mb-3">
          <div className="scanner-header">
            <button
              className="btn btn-glass btn-icon"
              onClick={() => navigate('/scanner/')}
            >
              <i className="fas fa-arrow-left"></i>
            </button>
            <div className="header-content">
              <div className="header-icon">
                <i className="fas fa-qrcode"></i>
              </div>
              <div className="header-text">
                <h1 className="text-primary mb-0">QR Scanner</h1>
                <p className="text-muted mb-0 small">{eventTitle}</p>
              </div>
            </div>
            <div className="btn btn-glass invisible">
              <i className="fas fa-arrow-left"></i>
            </div>
          </div>
        </div>

        {/* Scanner Container */}
        <div className="scanner-container">
          {/* Main Scanner Section */}
          <div className="scanner-main">
            <div className="scanner-viewport">
              <div className="scan-frame">
                <QRCodeScanner
                  key={facingMode}
                  onScan={handleScan}
                  onError={handleScanError}
                  facingMode={facingMode}
                  paused={!isScanning || !!scanResult}
                />
                {!isScanning && !scanResult && (
                  <div className="scanner-paused text-center d-flex align-items-center justify-content-center">
                    <p className="text-muted">Processing scan...</p>
                  </div>
                )}
              </div>
            </div>
            {/* Camera Controls */}
            <div className="scanner-controls">
              <div className="camera-controls">
                <div className="form-group">
                  <label htmlFor="cam-list" className="mb-2">Select Camera</label>
                  <select
                    id="cam-list"
                    className="form-select"
                    value={facingMode}
                    onChange={(e) => setFacingMode(e.target.value as 'user' | 'environment')}
                  >
                    <option value="environment">Back Camera</option>
                    <option value="user">Front Camera</option>
                  </select>
                </div>
                <button className="btn btn-primary" disabled>
                  <i className="fas fa-bolt"></i>
                  <span>Flash</span>
                </button>
              </div>
            </div>
          </div>

          {/* Side Section (History & Instructions for Desktop) */}
          <div className="scanner-side d-none d-lg-flex">
            {/* History Section */}
            <div className="scanner-history">
              <div className="history-header">
                <h5>
                  <i className="fas fa-history me-2"></i>Recent Scans
                </h5>
              </div>
              <div className="list-group list-group-flush">
                {recentScans.length === 0 ? (
                  <div className="list-group-item text-center text-muted">
                    <p className="mb-0">No recent scans</p>
                  </div>
                ) : (
                  recentScans.map((scan, index) => (
                    <div key={index} className="list-group-item">
                      <h6 className="mb-1">{scan.name}</h6>
                      <p>{scan.email}</p>
                      <code>{scan.identifier}</code>
                      {scan.seat_info && (
                        <p className="mb-1 text-primary">
                          <i className="fas fa-chair me-1"></i>{scan.seat_info.number}
                        </p>
                      )}
                      <p className="mb-1 text-secondary">{scan.category}</p>
                      {scan.scanned_on && (
                        <small className="text-muted">
                          <i className="fas fa-clock me-1"></i>{scan.scanned_on}
                        </small>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>
            {/* Instructions Section */}
            <div className="scanner-instructions">
              <div className="instructions-header">
                <h5>
                  <i className="fas fa-info-circle me-2"></i>Scanning Instructions
                </h5>
              </div>
              <div className="instructions-content">
                <ul className="list-unstyled">
                  <li>
                    <i className="fas fa-check-circle me-2"></i>
                    <span>Ensure good lighting</span>
                  </li>
                  <li>
                    <i className="fas fa-check-circle me-2"></i>
                    <span>Hold device steady</span>
                  </li>
                  <li>
                    <i className="fas fa-check-circle me-2"></i>
                    <span>Center QR code</span>
                  </li>
                  <li>
                    <i className="fas fa-check-circle me-2"></i>
                    <span>Use flash if needed</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Action Buttons (Mobile Only) */}
          <div className="scanner-actions mt-4 d-flex d-lg-none gap-2 justify-content-center">
            <button
              type="button"
              className="btn btn-glass"
              onClick={() => setShowHistoryModal(true)}
            >
              <i className="fas fa-history me-2"></i>View History
            </button>
            <button
              type="button"
              className="btn btn-glass"
              onClick={() => setShowInstructionsModal(true)}
            >
              <i className="fas fa-info-circle me-2"></i>Instructions
            </button>
          </div>
        </div>
      </Container>

      {/* Result Modal */}
      <Modal
        show={showResultModal}
        onHide={handleContinueScanning}
        centered
        size="lg"
        className="result-modal"
      >
        <Modal.Header
          className={scanResult?.success ? 'success' : 'error'}
          style={{
            backgroundColor: scanResult?.success ? 'rgba(25, 135, 84, 1)' : 'rgba(220, 53, 69, 1)',
          }}
        >
          <Modal.Title className="w-100 text-center result-title">
            {scanResult?.success ? 'Success' : 'Failed'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {scanResult && (
            <>
              {scanResult.photo_url && (
                <div className="text-center mb-3">
                  <img
                    src={scanResult.photo_url || ''}
                    alt="Pass"
                    className="img-fluid rounded"
                    style={{ maxHeight: '120px' }}
                  />
                </div>
              )}
              {formatScanResult(scanResult)}
            </>
          )}
        </Modal.Body>
        <Modal.Footer className="justify-content-center">
          <Button variant="success" onClick={handleContinueScanning} size="sm">
            <i className="fas fa-camera me-2"></i>Continue Scanning
          </Button>
        </Modal.Footer>
      </Modal>

      {/* History Modal (Mobile Only) */}
      <Modal
        show={showHistoryModal}
        onHide={() => setShowHistoryModal(false)}
        centered
        className="d-lg-none"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="fas fa-history me-2"></i>Recent Scans
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-0">
          {recentScans.length === 0 ? (
            <div className="p-3 text-center text-muted">
              <p className="mb-0">No recent scans</p>
            </div>
          ) : (
            <div className="list-group list-group-flush">
              {recentScans.map((scan, index) => (
                <div key={index} className="list-group-item glass">
                  <h6 className="mb-1">{scan.name}</h6>
                  <p>{scan.email}</p>
                  <code>{scan.identifier}</code>
                  {scan.seat_info && (
                    <p className="mb-1 text-primary">
                      <i className="fas fa-chair me-1"></i>{scan.seat_info.number}
                    </p>
                  )}
                  <p className="mb-1 text-secondary">{scan.category}</p>
                  {scan.scanned_on && (
                    <small className="text-muted">
                      <i className="fas fa-clock me-1"></i>{scan.scanned_on}
                    </small>
                  )}
                </div>
              ))}
            </div>
          )}
        </Modal.Body>
      </Modal>

      {/* Instructions Modal (Mobile Only) */}
      <Modal
        show={showInstructionsModal}
        onHide={() => setShowInstructionsModal(false)}
        centered
        className="d-lg-none"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="fas fa-info-circle me-2"></i>Scanning Instructions
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <ul className="list-unstyled instructions-list">
            <li>
              <i className="fas fa-check-circle text-success me-2"></i>
              <span>Ensure good lighting for better scanning</span>
            </li>
            <li>
              <i className="fas fa-check-circle text-success me-2"></i>
              <span>Hold your device steady while scanning</span>
            </li>
            <li>
              <i className="fas fa-check-circle text-success me-2"></i>
              <span>Position QR code within the camera frame</span>
            </li>
            <li>
              <i className="fas fa-check-circle text-success me-2"></i>
              <span>Use flash in low-light conditions</span>
            </li>
          </ul>
        </Modal.Body>
      </Modal>

      {/* Event Scanner Navigation */}
      <EventScannerNav />
    </div>
  );
};

export default QRCodeScannerPage;
